import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { io } from 'socket.io-client';
import { useAuth } from './AuthContext';
import { getSocketUrl } from '../utils/apiConfig';
import { isTokenExpired } from '../utils/authTokens';

const SocketContext = createContext();

// Module singleton so React Strict Mode remounts reuse one connection
// instead of aborting an in-flight WebSocket handshake.
let sharedSocket = null;
let sharedUserId = null;

export const useSocket = () => {
    const context = useContext(SocketContext);
    if (!context) {
        throw new Error('useSocket must be used within a SocketProvider');
    }
    return context;
};

function disconnectSharedSocket() {
    if (!sharedSocket) return;
    sharedSocket.removeAllListeners();
    sharedSocket.disconnect();
    sharedSocket = null;
    sharedUserId = null;
}

export const SocketProvider = ({ children }) => {
    const [socket, setSocket] = useState(sharedSocket);
    const [connected, setConnected] = useState(!!sharedSocket?.connected);
    const [error, setError] = useState(null);
    const { user, refreshAuthToken } = useAuth();
    const socketRef = useRef(sharedSocket);
    const userId = user?._id ? String(user._id) : null;

    // Keep listener callbacks pointed at the latest setState (survives provider remount)
    const setConnectedRef = useRef(setConnected);
    const setErrorRef = useRef(setError);
    const userLabelRef = useRef(user?.email || userId);
    const refreshAuthTokenRef = useRef(refreshAuthToken);
    refreshAuthTokenRef.current = refreshAuthToken;
    setConnectedRef.current = setConnected;
    setErrorRef.current = setError;
    userLabelRef.current = user?.email || userId;

    useEffect(() => {
        const token = localStorage.getItem('token');

        if (!token || !userId) {
            disconnectSharedSocket();
            socketRef.current = null;
            setSocket(null);
            setConnected(false);
            return;
        }

        // Different user logged in — drop the previous session socket
        if (sharedSocket && sharedUserId && sharedUserId !== userId) {
            disconnectSharedSocket();
        }

        // Reuse the existing live/connecting socket for this user
        if (sharedSocket && sharedUserId === userId) {
            socketRef.current = sharedSocket;
            setSocket(sharedSocket);
            setConnected(sharedSocket.connected);
            if (sharedSocket.disconnected) {
                sharedSocket.connect();
            }
            return;
        }

        const socketUrl = getSocketUrl();
        const isLocalhost = socketUrl.includes('localhost') || socketUrl.includes('127.0.0.1');

        const newSocket = io(socketUrl, {
            transports: ['websocket', 'polling'],
            timeout: 20000,
            // Reuse engine when possible — avoid aborting handshakes with forceNew
            forceNew: false,
            withCredentials: true,
            secure: !isLocalhost,
            rejectUnauthorized: !isLocalhost,
            // Send raw token; server also accepts optional 'Bearer ' prefix.
            // Read it on every (re)connect so a reconnect after a drop uses the
            // refreshed token, not the one this socket was created with. A token that has
            // expired (e.g. the laptop slept) is refreshed first rather than sent to be refused.
            auth: (cb) => {
                const current = localStorage.getItem('token') || token;
                if (!isTokenExpired(current)) {
                    cb({ token: current });
                    return;
                }
                refreshAuthTokenRef.current()
                    .then((fresh) => cb({ token: fresh }))
                    .catch(() => cb({ token: current }));
            },
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000
        });

        newSocket.on('connect', () => {
            console.info(`Socket connected to ${socketUrl} as ${userLabelRef.current}`);
            setConnectedRef.current(true);
            setErrorRef.current(null);
        });

        newSocket.on('disconnect', (reason) => {
            setConnectedRef.current(false);
            // Intentional client disconnects are expected (logout / HMR). Don't warn.
            if (reason !== 'io client disconnect') {
                console.warn(`Socket disconnected from ${socketUrl}: ${reason}`);
            }
        });

        newSocket.on('connect_error', (err) => {
            // Aborted handshakes during Strict Mode / fast remounts are noise
            const message = err?.message || String(err);
            if (
                message.includes('closed before the connection is established') ||
                message.includes('websocket error')
            ) {
                return;
            }
            console.error(`Socket connection error (${socketUrl}):`, message);
            setErrorRef.current('Connection failed');
            setConnectedRef.current(false);
        });

        newSocket.on('error', (err) => {
            console.error('Socket error:', err);
            setErrorRef.current(err?.message || 'Socket error');
        });

        sharedSocket = newSocket;
        sharedUserId = userId;
        socketRef.current = newSocket;
        setSocket(newSocket);

        // Keep the singleton alive across Strict Mode remounts.
        // Real teardown happens on logout (token/userId cleared) above.
        return undefined;
    }, [userId]);

    // Queue-related socket methods
    const joinQueue = (boothId, eventId) => {
        if (socket && connected) {
            socket.emit('join-queue', { boothId, eventId });
        }
    };

    const leaveQueue = (queueId, reason) => {
        if (socket && connected) {
            socket.emit('leave-queue', { queueId, reason });
        }
    };

    const subscribeToQueueUpdates = (queueId, callback) => {
        if (socket && connected) {
            socket.on(`queue-update-${queueId}`, callback);
        }
    };

    const unsubscribeFromQueueUpdates = (queueId, callback) => {
        if (socket && connected) {
            socket.off(`queue-update-${queueId}`, callback);
        }
    };

    // Call-related socket methods
    const joinCall = (roomId, userRole) => {
        if (socket && connected) {
            socket.emit('join-call', { roomId, userRole });
        }
    };

    const leaveCall = (roomId) => {
        if (socket && connected) {
            socket.emit('leave-call', { roomId });
        }
    };

    const requestInterpreter = (meetingId, interpreterType) => {
        if (socket && connected) {
            socket.emit('request-interpreter', { meetingId, interpreterType });
        }
    };

    const acceptInterpreterRequest = (meetingId) => {
        if (socket && connected) {
            socket.emit('accept-interpreter-request', { meetingId });
        }
    };

    // Chat-related socket methods
    const sendMessage = (roomId, message, type = 'text') => {
        if (socket && connected) {
            socket.emit('send-message', { roomId, message, type });
        }
    };

    const subscribeToMessages = (roomId, callback) => {
        if (socket && connected) {
            socket.on(`messages-${roomId}`, callback);
        }
    };

    const unsubscribeFromMessages = (roomId, callback) => {
        if (socket && connected) {
            socket.off(`messages-${roomId}`, callback);
        }
    };

    const value = {
        socket,
        connected,
        error,
        joinQueue,
        leaveQueue,
        subscribeToQueueUpdates,
        unsubscribeFromQueueUpdates,
        joinCall,
        leaveCall,
        requestInterpreter,
        acceptInterpreterRequest,
        sendMessage,
        subscribeToMessages,
        unsubscribeFromMessages
    };

    return (
        <SocketContext.Provider value={value}>
            {children}
        </SocketContext.Provider>
    );
};
